import { useMemo } from "react";
import { useEmberlee } from "@um/ui";
import {
  getPersona,
  PersonaImageCollection,
  type PersonaImageInfo,
} from "@um/contracts";

import { useRewardMind } from "./useRewardMind";
import { useCharactersStore } from "@/store/useCharactersStore";

/**
 * The one character the reward loop is about: the mind's captain, falling back to the newest
 * character where no captain is set.
 */
export interface Companion {
  character:
    | ReturnType<typeof useCharactersStore.getState>["characters"][number]
    | undefined;
  persona: ReturnType<typeof getPersona> | undefined;
  /** Always safe to render; falls back to a generic label. */
  name: string;
  /**
   * One panel of the companion's expression sheet, falling back to the stand-in
   * character for accounts that have no companion yet.
   */
  panel: (variationId: string) => PersonaImageInfo | null;
}

export function useCompanion(): Companion {
  const userMind = useRewardMind();
  const characters = useCharactersStore((s) => s.characters);
  const { lookup: emberleeLookup } = useEmberlee();
  const captainId = userMind?.defaultAnimal?.id;

  return useMemo(() => {
    const visible = (characters ?? []).filter(
      (c) => !c.isDeleted && !c.isHidden && !c.isSelfProfile
    );
    const character =
      (captainId && visible.find((c) => c.ref.id === captainId)) ||
      visible[0];
    const persona = character ? getPersona(character) : undefined;
    return {
      character,
      persona,
      name: persona?.name || character?.name || "your companion",
      // `lookup` falls back to the persona's own default, so v1 personas and
      // sheets still generating still render.
      panel: (variationId: string) =>
        persona
          ? new PersonaImageCollection(persona).lookup(variationId)
          : emberleeLookup(variationId),
    };
  }, [characters, captainId, emberleeLookup]);
}
